import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import {
  type PropsWithChildren,
  type TouchEvent as ReactTouchEvent,
  useEffect,
  useRef,
  useState
} from 'react'

import { InformationIcon } from '../../icons'

const GAP_AFTER_BUTTON = 8 // px between button and panel
const SIDE_PADDING = 16 // px bottom margin from viewport edge

const InformationToggle = ({ children }: PropsWithChildren): JSX.Element => {
  const [showInformation, setShowInformation] = useState<boolean>(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLDivElement | null>(null)

  // dynamic position / size
  const [panelTop, setPanelTop] = useState<number>(72)
  const [panelMaxH, setPanelMaxH] = useState<number>(0)

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = showInformation ? 'hidden' : ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [showInformation])

  // Compute panel top + available height (near the button)
  useEffect(() => {
    if (!showInformation) return

    const recompute = (): void => {
      const btn = triggerRef.current
      const topFromViewport = btn ? btn.getBoundingClientRect().bottom + GAP_AFTER_BUTTON : 72

      const vh = window.innerHeight // approximates 100dvh
      const maxH = Math.max(0, vh - topFromViewport - SIDE_PADDING)

      setPanelTop(topFromViewport)
      setPanelMaxH(maxH)
    }

    recompute()
    window.addEventListener('resize', recompute)
    window.addEventListener('orientationchange', recompute)
    return () => {
      window.removeEventListener('resize', recompute)
      window.removeEventListener('orientationchange', recompute)
    }
  }, [showInformation])

  // Trap scroll inside modal content (iOS)
  useEffect(() => {
    const el = scrollRef.current
    if (!showInformation || !el) return

    let lastY = 0

    const handleTouchStart = (e: TouchEvent): void => {
      lastY = e.touches[0].clientY
    }

    const handleTouchMove = (e: TouchEvent): void => {
      const currentY = e.touches[0].clientY
      const deltaY = currentY - lastY
      lastY = currentY

      const atTop = el.scrollTop <= 0
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight

      if ((atTop && deltaY > 0) || (atBottom && deltaY < 0)) {
        e.preventDefault()
      }
    }

    el.addEventListener('touchstart', handleTouchStart, { passive: true })
    el.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      el.removeEventListener('touchstart', handleTouchStart)
      el.removeEventListener('touchmove', handleTouchMove)
    }
  }, [showInformation])

  return (
    <div>
      {/* trigger */}
      <div
        ref={triggerRef}
        onClick={() => setShowInformation((prev) => !prev)}
        className="w-10 h-10 p-1 rounded-full bg-custom-gradient absolute -top-4 right-0 justify-center items-center cursor-pointer hidden mobile:flex z-50 drop-shadow-2xl"
      >
        <InformationIcon color={tailwindColors.white.DEFAULT} className="w-7 h-7" />
      </div>

      {/* backdrop */}
      {showInformation && (
        <div
          className="fixed inset-0 bg-black opacity-50 z-40 hidden mobile:flex overscroll-none"
          onClick={() => setShowInformation(false)}
          onWheel={(e: React.WheelEvent<HTMLDivElement>) => e.preventDefault()}
          onTouchMove={(e: ReactTouchEvent<HTMLDivElement>) => e.preventDefault()}
          role="presentation"
        />
      )}

      {/* floating panel near the button; height = available space */}
      {showInformation && (
        <div
          ref={scrollRef}
          className="
            fixed z-50 
            left-1/2 -translate-x-1/2
            bg-white rounded-lg drop-shadow-2xl
            hidden mobile:flex mobile:flex-col
            w-11/12
            overflow-auto mobile:overscroll-contain [WebkitOverflowScrolling:touch]
            p-4 pb-8
          "
          style={{
            top: `${panelTop}px`,
            maxHeight: `${panelMaxH}px`
          }}
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export default InformationToggle
